import { Link } from "react-router-dom";
import { ArrowRight, Globe2, PoundSterling, Scale, FileText, Languages, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";

const FOUNDER_IMG = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=85&w=800&auto=format&fit=crop";
const OFFICE_IMG = "https://images.unsplash.com/photo-1758518727820-28491c194bee?crop=entropy&cs=srgb&fm=jpg&q=85";

const SPECIALISATIONS = [
  { icon: FileText, title: "ATS Keyword Tailoring", text: "Every document is parsed against the logic used by Workday, Taleo and Greenhouse — the platforms that dominate UK enterprise hiring." },
  { icon: Globe2, title: "Student & Skilled Worker Visas", text: "Graduate visa timing, Skilled Worker sponsorship positioning, and targeting the Home Office register of licensed sponsors in your sector." },
  { icon: Languages, title: "British English Standards", text: "Spelling, idiom and tone calibrated to what British hiring managers expect — not Americanised templates." },
  { icon: ShieldCheck, title: "GDPR-Aware Documents", text: "No photos, no date of birth, no unnecessary personal data — formatted to modern UK privacy conventions." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="mx-auto max-w-7xl px-5 pb-20 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">Why RMS Group</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="about-headline">
          <MaskedLine delay={0.1}>London-grade insight.</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">Without the London fees.</MaskedLine>
        </h1>
      </section>

      {/* FOUNDER STORY */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8" data-testid="founder-story-section">
        <div className="grid items-start gap-14 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl border border-[#C5A059]/30" />
              <img src={FOUNDER_IMG} alt="RMS Group founder, UK HR professional" className="relative aspect-[4/5] w-full rounded-2xl object-cover object-top" data-testid="founder-image" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <h2 className="font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">The professional story</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-300">
              <p>
                Before founding RMS Group, our lead consultant spent years in UK corporate HR — sitting on the other side of the table, screening thousands of applications for British employers.
              </p>
              <p>
                A pattern became impossible to ignore: exceptional international students — engineers, clinicians, analysts, business graduates — were being rejected in the first ten seconds. Not for lack of ability, but because their CVs broke unwritten British rules. Home-country formats instead of the two-page standard. Photos and personal details UK recruiters consider red flags. Foreign degree titles nobody could parse. Layouts that Applicant Tracking Systems scrambled into unreadable noise.
              </p>
              <p>
                RMS Group — Resume Making Services Group — was established to fix exactly that, with international students at the heart of everything we do. Headquartered in Sheffield, United Kingdom, with a branch office in Hyderabad, India, every document is written through the lens of someone who has actually scored CVs for UK employers, sat on British interview panels, and knows what makes a hiring manager keep reading.
              </p>
              <p className="font-serif text-xl italic text-[#C5A059]">
                "We don't guess what UK recruiters want. We were the recruiters."
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFSHORE MODEL */}
      <section className="mt-24 border-y border-slate-800/60 bg-[#0C1222]" data-testid="offshore-model-section">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <span className="overline-label">The RMS Model</span>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-slate-50 sm:text-4xl">
                How you get £250-quality work for £77
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-300">
                A London CV agency pays London rents, London salaries and London overheads — and passes all of it to you. RMS Group is headquartered in Sheffield with a supporting branch in Hyderabad, India, so our cost base stays lean while the expertise stays firmly British. You get UK recruiting leadership without the London markup.
              </p>
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-700/50 p-6">
                  <div className="flex items-center gap-2 text-slate-400"><Scale size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">London agency</span></div>
                  <div className="mt-3 font-mono text-3xl text-slate-500 line-through">£250+</div>
                  <div className="mt-1 text-xs text-slate-500">per CV rewrite</div>
                </div>
                <div className="gold-stroke rounded-xl border bg-[#111827] p-6">
                  <div className="flex items-center gap-2 text-[#C5A059]"><PoundSterling size={16} /><span className="font-mono text-[10px] uppercase tracking-[0.2em]">RMS Group</span></div>
                  <div className="mt-3 font-mono text-3xl font-semibold text-[#C5A059]">£77</div>
                  <div className="mt-1 text-xs text-slate-400">same UK standards, same rigour</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <img src={OFFICE_IMG} alt="Professional consultation" className="aspect-[4/3] w-full rounded-2xl border border-slate-700/50 object-cover" data-testid="offshore-image" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SPECIALISATIONS */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="specialisations-section">
        <Reveal>
          <span className="overline-label">UK Market Specialisation</span>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">
            Built exclusively for the British job market
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SPECIALISATIONS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <div className="card-surface h-full p-8" data-testid={`specialisation-card-${i}`}>
                <s.icon size={24} className="text-[#C5A059]" />
                <h3 className="mt-5 font-serif text-xl font-semibold text-slate-50">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 text-center">
            <Link to="/order" className="btn-gold" data-testid="about-cta-order-btn">
              Work With Us <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
